import type {
  FollowUpActionResponseDto,
  NurseryItemDtoStatus,
  NurseryResponseDtoStatus,
  ScheduledQuestionnaireListItemDto,
} from "@/generated/model";
import {
  ComplianceTaskInstanceResponseDtoStatus,
  EventRecordStatus,
  FollowUpActionResponseDtoStatus,
  ScheduledQuestionnaireListItemDtoMissBehaviour,
} from "@/generated/model";
import type { ScheduledQuestionnaireListItemWithSync } from "@/hooks/use-offline-scheduled-questionnaires";
import { type DisplayStatus, getDisplayStatus } from "@/lib/checklist-utils";
import { isUkToday } from "@/lib/date-utils";
import { isActionOverdue } from "@/lib/follow-up-action-utils";

/**
 * Unified traffic-light status pill for Workspace rows (NAU-316):
 * Complete = success (green), Due = warning (orange), Late/Missed = error
 * (red). Neutral is reserved for rows that aren't actionable yet (Upcoming)
 * or are closed without completion (Cancelled).
 */
export type StatusPillTone = "success" | "warning" | "error" | "neutral";

export type StatusPillIcon = "check" | "x" | "alert" | "paperclip";

export interface StatusPill {
  tone: StatusPillTone;
  label: string;
  icon?: StatusPillIcon;
}

/**
 * Pure checklist mapping. A late completion still reads Completed/green —
 * red is reserved for outstanding work; the row's time text keeps showing
 * "N days late". A mid-window long-cadence item labels "Due" rather than
 * "Due today" (NAU-233).
 */
export function checklistPillFromStatus(
  status: DisplayStatus,
  opts: {
    deadlineIsToday: boolean;
    scheduledIsToday: boolean;
    isHardMiss: boolean;
  },
): StatusPill {
  switch (status) {
    case "completed":
    case "completed_syncing":
    case "completed_late":
      return { tone: "success", label: "Completed", icon: "check" };
    case "in_progress":
      return { tone: "warning", label: "In progress" };
    case "due":
      return {
        tone: "warning",
        label: opts.deadlineIsToday ? "Due today" : "Due",
      };
    case "overdue":
      // A hard-missed window can't be completed any more — that's Missed,
      // not Late, even though the server status is still open.
      return opts.isHardMiss
        ? { tone: "error", label: "Missed", icon: "x" }
        : { tone: "error", label: "Late", icon: "alert" };
    case "missed":
      return { tone: "error", label: "Missed", icon: "x" };
    case "pending":
      return opts.scheduledIsToday
        ? { tone: "neutral", label: "Due today" }
        : { tone: "neutral", label: "Upcoming" };
  }
}

export function checklistStatusPill(
  item:
    | ScheduledQuestionnaireListItemDto
    | ScheduledQuestionnaireListItemWithSync,
): StatusPill {
  return checklistPillFromStatus(getDisplayStatus(item), {
    deadlineIsToday: isUkToday(new Date(item.availableUntil)),
    scheduledIsToday: isUkToday(new Date(item.scheduledDate)),
    isHardMiss:
      item.missBehaviour ===
      ScheduledQuestionnaireListItemDtoMissBehaviour.HARD_MISS,
  });
}

export function followUpActionPill(
  action: FollowUpActionResponseDto,
): StatusPill {
  if (action.status === FollowUpActionResponseDtoStatus.COMPLETED) {
    return { tone: "success", label: "Completed", icon: "check" };
  }
  if (action.status === FollowUpActionResponseDtoStatus.CANCELLED) {
    return { tone: "neutral", label: "Cancelled" };
  }
  if (action.status === FollowUpActionResponseDtoStatus.WAITING_APPROVAL) {
    return { tone: "warning", label: "Waiting approval" };
  }
  if (isActionOverdue(action)) {
    return { tone: "error", label: "Late", icon: "alert" };
  }
  return { tone: "warning", label: "Open" };
}

/**
 * Monitoring page pill (NAU-318): Active maps to the Action required (red)
 * traffic-light state, Resolved to On track (green) — per the ticket's
 * sign-off assumptions.
 *
 * Failed pass/fail checks are the exception: they band amber
 * on the dashboard rather than red, so their pill is amber too and the table
 * agrees with the Today's status row. Temperature breaches stay red.
 */
export function monitoringAlertPill(
  status: "ACTIVE" | "RESOLVED",
  isCheckFailure = false,
): StatusPill {
  if (status !== "ACTIVE") {
    return { tone: "success", label: "Resolved", icon: "check" };
  }
  return isCheckFailure
    ? { tone: "warning", label: "Active", icon: "alert" }
    : { tone: "error", label: "Active", icon: "alert" };
}

/**
 * Untargeted Monitoring rows (NAU-377) — a garden welfare check, a sleep check
 * or a food temperature. Neutral rather than green: the check was recorded, but
 * there is no target behind it, so "Resolved"/On track would assert a judgement
 * nothing made.
 */
export const MONITORING_UNTARGETED_PILL: StatusPill = {
  tone: "neutral",
  label: "Recorded",
};

/**
 * Nursery lifecycle status badge (NAU-184): Active = success (green),
 * Disabled = warning (orange), Archived = neutral (grey).
 */
export function nurseryStatusPill(
  status: NurseryItemDtoStatus | NurseryResponseDtoStatus,
): StatusPill {
  switch (status as string) {
    case "ACTIVE":
      return { tone: "success", label: "Active" };
    case "DISABLED":
      return { tone: "warning", label: "Disabled" };
    default:
      return { tone: "neutral", label: "Archived" };
  }
}

/**
 * Events Hub status pill (NAU-341). The ticket's colour key is red awaiting
 * acknowledgement, amber in progress, green resolved, **blue** archived.
 *
 * Archived reads grey, not blue. There is no blue tone in this palette, and the
 * nearest thing — `brand` on the Subframe Badge — already means "In progress" on
 * the Checklist Manager, so introducing it here would make one colour mean two
 * things and leave "Archived" grey for nurseries but blue for events. Grey also
 * matches `nurseryStatusPill` below, which maps ARCHIVED → neutral today.
 *
 * NAU-344 is agreeing one standard set of status labels and colours with Debbie
 * and is the right place to introduce a blue tone across every surface at once.
 * The API returns semantic states only, so settling it means editing this
 * function and nothing else.
 */
export function eventRecordPill(status: EventRecordStatus): StatusPill {
  switch (status) {
    case EventRecordStatus.IN_PROGRESS:
      return { tone: "warning", label: "In progress" };
    case EventRecordStatus.RESOLVED:
      return { tone: "success", label: "Resolved", icon: "check" };
    case EventRecordStatus.ARCHIVED:
      return { tone: "neutral", label: "Archived" };
    default:
      return {
        tone: "error",
        label: "Awaiting acknowledgement",
        icon: "alert",
      };
  }
}

export function complianceInstancePill(
  status: ComplianceTaskInstanceResponseDtoStatus,
): StatusPill {
  switch (status) {
    case ComplianceTaskInstanceResponseDtoStatus.OVERDUE:
      return { tone: "error", label: "Late", icon: "alert" };
    case ComplianceTaskInstanceResponseDtoStatus.DUE_SOON:
      return { tone: "warning", label: "Due soon" };
    case ComplianceTaskInstanceResponseDtoStatus.AWAITING_EVIDENCE:
      return {
        tone: "warning",
        label: "Awaiting certificate",
        icon: "paperclip",
      };
    case ComplianceTaskInstanceResponseDtoStatus.COMPLETED:
      return { tone: "success", label: "Completed", icon: "check" };
    case ComplianceTaskInstanceResponseDtoStatus.CANCELLED:
      return { tone: "neutral", label: "Cancelled" };
    default:
      return { tone: "neutral", label: "Upcoming" };
  }
}

/** A request voided by a newer version, or a record signed against an older one (AC23). */
export const ACKNOWLEDGEMENT_SUPERSEDED_PILL: StatusPill = {
  tone: "neutral",
  label: "Superseded",
};

/**
 * AC15's two states, as one pill for a row.
 *
 * Read and Signed are separate facts, so a single pill has to pick which one to
 * lead with. It leads with the terminal state (Signed), then with whether the
 * deadline has passed, and only then with Read — because a manager scanning the
 * roster is looking for who has NOT finished, and "Read · not signed" is the
 * row that needs chasing hardest: they have seen it and not committed.
 *
 * No blue tone: StatusPillTone has none, deliberately, and adding one here would
 * make a colour mean two things across the app.
 */
export function acknowledgementRequestPill(
  request: {
    state: string;
    readAt?: string | Date | null;
    signedAt?: string | Date | null;
    dueAt?: string | Date | null;
  },
  now: Date = new Date(),
): StatusPill {
  if (request.signedAt) {
    return { tone: "success", label: "Signed", icon: "check" };
  }
  if (request.state === "VOIDED") {
    return ACKNOWLEDGEMENT_SUPERSEDED_PILL;
  }

  const overdue = request.dueAt
    ? new Date(request.dueAt).getTime() < now.getTime()
    : false;

  if (request.readAt) {
    return overdue
      ? { tone: "error", label: "Read · not signed", icon: "alert" }
      : { tone: "warning", label: "Read · not signed" };
  }
  if (overdue) {
    return { tone: "error", label: "Overdue", icon: "alert" };
  }
  return { tone: "warning", label: "Not read" };
}

/**
 * The same two states as a PAIR, for tables and tiles that must report both
 * (AC15/AC24). One pill has to collapse them; this does not.
 */
export function acknowledgementTwoStatePills(request: {
  readAt?: string | Date | null;
  signedAt?: string | Date | null;
}): { read: StatusPill; signed: StatusPill } {
  return {
    read: readPill(request),
    signed: request.signedAt
      ? { tone: "success", label: "Signed", icon: "check" }
      : { tone: "warning", label: "Not signed" },
  };
}

/**
 * Signing now stamps Read at the same moment, so signed-but-unread can only be
 * a request migrated from the pre-NAU-410 model, which never captured Read.
 */
function readPill(request: {
  readAt?: string | Date | null;
  signedAt?: string | Date | null;
}): StatusPill {
  if (request.readAt) {
    return { tone: "success", label: "Read", icon: "check" };
  }
  if (request.signedAt) {
    return { tone: "neutral", label: "Read not recorded" };
  }
  return { tone: "neutral", label: "Not read" };
}
